import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { db } from '../lib/supabase'
import LoadingSpinner from '../components/LoadingSpinner'
import { parseLocalDate } from '../utils/dateUtils'

export default function DescuentoDetalle() {
  const { id } = useParams()
  const [descuento, setDescuento] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const { data } = await db.getDescuentoById(id)
      setDescuento(data || null)
      setLoading(false)
    }
    load()
  }, [id])

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = parseLocalDate(dateString)
    return date.toLocaleDateString('es-PE', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  if (loading) {
    return (
      <div style={{
        maxWidth: 'var(--container-max-width)',
        margin: '0 auto',
        padding: '0 2rem',
        minHeight: '400px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '3rem 0'
        }}>
          <LoadingSpinner message="Cargando descuento..." size={48} />
        </div>
      </div>
    )
  }

  if (!descuento) {
    return (
      <div style={{
        maxWidth: 'var(--container-max-width)',
        margin: '0 auto',
        padding: '0 2rem'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)',
          border: '1px solid #FCA5A5',
          borderRadius: 'var(--radius-md)',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--font-size-display-l)',
            fontWeight: 'var(--font-weight-bold)',
            color: '#991B1B',
            margin: '0 0 1rem 0'
          }}>Descuento no encontrado</h2>
          <p style={{
            fontFamily: 'var(--font-ui)',
            color: '#991B1B',
            margin: '0 0 1.5rem 0'
          }}>El descuento que buscas no existe o ha sido eliminado.</p>
          <Link
            to="/descuentos"
            style={{
              display: 'inline-block',
              background: 'var(--ctaBg)',
              color: 'var(--ctaText)',
              padding: '0.75rem 1.5rem',
              borderRadius: 'var(--radius-md)',
              textDecoration: 'none',
              fontFamily: 'var(--font-ui)',
              fontWeight: 'var(--font-weight-medium)'
            }}
          >
            Ver todos los descuentos
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{descuento.meta_title || descuento.titulo}</title>
        <meta name="description" content={descuento.meta_description || 'Descuento vigente en Plaza Aurora'} />
        <link rel="canonical" href={descuento.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/descuentos/${descuento.id}`} />
        <meta name="robots" content={descuento.robots_index === false ? 'noindex,nofollow' : 'index,follow'} />
        <meta property="og:title" content={descuento.meta_title || descuento.titulo} />
        <meta property="og:description" content={descuento.meta_description || 'Descuento vigente en Plaza Aurora'} />
        {descuento.og_image_url && <meta property="og:image" content={descuento.og_image_url} />}
      </Helmet>

      {descuento.json_ld && (
        <script type="application/ld+json">{descuento.json_ld}</script>
      )}

      <main>
        <div style={{ maxWidth: 'var(--container-max-width)', margin: '0 auto', padding: '0 2rem' }}>
          {/* Breadcrumb */}
          <nav style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-small)',
            marginBottom: '1.5rem',
            marginTop: '1rem'
          }}>
            <Link 
              to="/" 
              style={{
                color: 'var(--color-text-primary)',
                textDecoration: 'none',
                transition: 'text-decoration 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Inicio
            </Link>
            <span style={{ margin: '0 0.5rem', color: 'var(--color-text-muted)' }}>/</span>
            <Link 
              to="/descuentos" 
              style={{
                color: 'var(--color-text-primary)',
                textDecoration: 'none',
                transition: 'text-decoration 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Descuentos
            </Link>
            <span style={{ margin: '0 0.5rem', color: 'var(--color-text-muted)' }}>/</span>
            <span style={{ color: 'var(--color-text-muted)' }}>{descuento.titulo}</span>
          </nav>

          {/* Grid 2 columnas */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth >= 1024 ? '2fr 1fr' : '1fr',
            gap: '2rem',
            marginBottom: '4rem'
          }}>
            {/* Columna Principal */}
            <div>
              {/* Imagen Principal */}
              <div style={{
                position: 'relative',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                background: 'var(--color-accent-gray)',
                marginBottom: '1.5rem'
              }}>
                {descuento.imagen_url ? (
                  <img
                    src={descuento.imagen_url}
                    alt={descuento.imagen_alt || descuento.titulo}
                    style={{
                      width: '100%',
                      aspectRatio: '1140/618',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    aspectRatio: '1140/618',
                    background: 'white',
                    border: '2px dashed #D1D5DB'
                  }}>
                  </div>
                )}
              </div>

              {/* Título */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h1 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--font-size-display-l)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 0.75rem 0',
                  lineHeight: '1.1',
                  letterSpacing: '0.02em'
                }}>{descuento.titulo}</h1>
              </div>

              {/* Descripción Card - Always visible */}
              <div style={{
                background: 'var(--surface)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'none',
                border: 'var(--card-border)',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h2 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--font-size-h3)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 1rem 0',
                  letterSpacing: '0.02em'
                }}>DESCRIPCIÓN</h2>
                {descuento.descripcion ? (
                  <p style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-text-primary)',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-line',
                    margin: 0
                  }}>
                    {descuento.descripcion}
                  </p>
                ) : (
                  <div style={{
                    minHeight: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                    borderRadius: 'var(--radius-md)',
                    border: '2px dashed #D1D5DB'
                  }}>
                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                      <svg style={{ width: '48px', height: '48px', color: '#9CA3AF', margin: '0 auto 0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        color: '#9CA3AF',
                        margin: 0
                      }}>Sin descripción</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Marcas Participantes Card */}
              {Array.isArray(descuento.descuentos_marcas) && descuento.descuentos_marcas.length > 0 && (
                <div style={{
                  background: 'var(--surface)',
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'none',
                  border: 'var(--card-border)',
                  padding: '1.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <h2 style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--h2)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--color-text-primary)',
                    margin: '0 0 1rem 0'
                  }}>Marcas Participantes</h2>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: '1rem'
                  }}>
                    {descuento.descuentos_marcas.map((m) => {
                      const tienda = m.tiendas
                      const isPublished = tienda?.publicado
                      
                      const logoContent = (m.tienda_id && tienda) ? (
                        tienda.logo_url ? (
                          <img
                            src={tienda.logo_url}
                            alt={tienda.nombre}
                            style={{
                              maxHeight: '100%',
                              maxWidth: '100%',
                              objectFit: 'contain',
                              borderRadius: 'var(--radius-md)'
                            }}
                          />
                        ) : (
                          <span style={{
                            fontFamily: 'var(--font-ui)',
                            fontSize: 'var(--font-size-small)',
                            fontWeight: 'var(--font-weight-medium)',
                            color: 'var(--color-text-primary)',
                            textAlign: 'center'
                          }}>
                            {tienda.nombre}
                          </span>
                        )
                      ) : (
                        m.brand_logo_url ? (
                          <img
                            src={m.brand_logo_url}
                            alt={m.brand_name || 'Marca'}
                            style={{
                              maxHeight: '100%',
                              maxWidth: '100%',
                              objectFit: 'contain',
                              borderRadius: 'var(--radius-md)'
                            }}
                          />
                        ) : (
                          <span style={{
                            fontFamily: 'var(--font-ui)',
                            fontSize: 'var(--font-size-small)',
                            fontWeight: 'var(--font-weight-medium)',
                            color: 'var(--color-text-primary)',
                            textAlign: 'center'
                          }}>
                            {m.brand_name}
                          </span>
                        )
                      )

                      const cardStyle = {
                        background: 'white',
                        borderRadius: 'var(--radius-md)',
                        padding: '0',
                        aspectRatio: '1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textDecoration: 'none',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        boxShadow: 'none',
                        border: 'var(--card-border)',
                        overflow: 'hidden'
                      }

                      // If tienda is published, render as clickable Link
                      if (isPublished && tienda) {
                        return (
                          <Link
                            key={m.id}
                            to={`/marcas/${tienda.id}`}
                            style={cardStyle}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)'
                            }}
                          >
                            {logoContent}
                          </Link>
                        )
                      }

                      // Otherwise, render as non-clickable div
                      return (
                        <div
                          key={m.id}
                          style={{
                            ...cardStyle,
                            cursor: 'default',
                            opacity: tienda && !isPublished ? 0.7 : 1
                          }}
                        >
                          {logoContent}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Columna Lateral (Sidebar) */}
            <div style={{
              alignSelf: 'flex-start',
              position: window.innerWidth >= 1024 ? 'sticky' : 'static',
              top: window.innerWidth >= 1024 ? '6rem' : 'auto'
            }}>
              {/* Información del Descuento */}
              <div style={{
                background: 'var(--surface)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'none',
                border: 'var(--card-border)',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h2 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--h2)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 1rem 0'
                }}>Información del Descuento</h2>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {/* Fecha de inicio */}
                  {descuento.fecha_inicio && (
                    <div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'var(--color-text-muted)',
                        marginBottom: '0.25rem'
                      }}>
                        <svg style={{ width: '20px', height: '20px', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span style={{
                          fontFamily: 'var(--font-ui)',
                          fontSize: 'var(--font-size-small)',
                          fontWeight: 'var(--font-weight-medium)'
                        }}>Fecha de inicio:</span>
                      </div>
                      <p style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-body)',
                        color: 'var(--color-text-primary)',
                        marginLeft: '1.75rem',
                        margin: 0
                      }}>{formatDate(descuento.fecha_inicio)}</p>
                    </div>
                  )}

                  {/* Fecha de fin */}
                  {descuento.fecha_fin && (
                    <div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'var(--color-text-muted)',
                        marginBottom: '0.25rem'
                      }}>
                        <svg style={{ width: '20px', height: '20px', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span style={{
                          fontFamily: 'var(--font-ui)',
                          fontSize: 'var(--font-size-small)',
                          fontWeight: 'var(--font-weight-medium)'
                        }}>Fecha de fin:</span>
                      </div>
                      <p style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-body)',
                        color: 'var(--color-text-primary)',
                        marginLeft: '1.75rem',
                        margin: 0
                      }}>{formatDate(descuento.fecha_fin)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
